import L from 'leaflet';

/**
 * Fælles opsætning for Leaflet-kortet i PWA'en.
 *
 * Baggrund: CARTO lukkede for anonym brug af deres basemaps og serverer nu
 * fliser med et "API KEY REQUIRED"-vandmærke, så vi er skiftet til OpenStreetMap.
 * OSM kræver synlig kreditering - attribution-kontrollen må derfor ikke skjules.
 */
export const OSRM_SERVICE_URL = 'https://tdkosrmmapsservice.azurewebsites.net/api/TdkOsrmMapsService';

/**
 * leaflet-routing-machine sender kun continue_straight videre til OSRM, hvis
 * allowUTurns er sat (se osrm-v1.js: buildRouteUrl). Uden den bruger OSRM sin
 * default, hvor U-vending ved via-punkter er forbudt: er et støjfyldt GPS-punkt
 * snappet til en sidegade, SKAL ruten køre ind i gaden, rundt om blokken og ud
 * igen. Det er årsagen til de vilkårlige omveje ind ad sidegader.
 */
export const OSRM_ROUTING_OPTIONS = {
  alternatives: false,
  allowUTurns: true
};

const OSM_TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const OSM_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';

/** Lægger standard-baggrundskortet på et kort. */
export function addBaseTileLayer (map: L.Map): L.TileLayer {
  return L.tileLayer(OSM_TILE_URL, {
    attribution: OSM_ATTRIBUTION,
    maxZoom: 19
  }).addTo(map);
}

/**
 * Leaflet måler kun containerens størrelse ved oprettelse og ved window-resize.
 * Kortet sidder i et flex-layout, der først falder på plads når data er hentet -
 * uden dette hentes fliser ikke til det nye område (grå felter), og klik rammer
 * forskudt. Returnerer observeren, så kalderen kan disconnecte den.
 */
export function observeMapResize (map: L.Map, element: HTMLElement): ResizeObserver | null {
  if (typeof ResizeObserver === 'undefined') {
    return null;
  }

  const observer = new ResizeObserver(() => map.invalidateSize({ animate: false }));
  observer.observe(element);
  return observer;
}

/**
 * Gemte sikre ruter er rå GPS-spor med punkter få meter fra hinanden. Sendes de
 * 1:1 til OSRM som via-punkter, snapper hvert punkt til nærmeste vej, og støj
 * omkring kryds giver forkerte snap og omveje. Udtynder sporet, så punkterne
 * ligger mindst minMeters fra hinanden. Endepunkterne bevares altid.
 */
export function thinRouteWaypoints (
  points: L.LatLng[],
  minMeters = 50,
  maxPoints = 150
): L.LatLng[] {
  if (points.length <= 2) {
    return [...points];
  }

  const thinned: L.LatLng[] = [points[0]];

  for (let i = 1; i < points.length - 1; i++) {
    if (points[i].distanceTo(thinned[thinned.length - 1]) >= minMeters) {
      thinned.push(points[i]);
    }
  }

  thinned.push(points[points.length - 1]);

  return capWaypoints(thinned, maxPoints);
}

/** OSRM afviser meget lange via-lister. Udtager jævnt fordelte punkter som sikkerhedsventil. */
function capWaypoints (points: L.LatLng[], maxPoints: number): L.LatLng[] {
  if (points.length <= maxPoints) {
    return [...points];
  }

  const step = (points.length - 1) / (maxPoints - 1);
  const capped: L.LatLng[] = [];

  for (let i = 0; i < maxPoints; i++) {
    capped.push(points[Math.round(i * step)]);
  }

  return capped;
}

/* ---------------------------------------------------------------------------
   Fjernelse af falske omveje (snap-artefakter)

   Gemte rutepunkter er allerede snappet til vejnettet én gang, før de blev
   gemt. Ligger et punkt på den forkerte vognbane af en delt vej - typisk lige
   ved en til- eller frakørsel - kan OSRM kun ankomme fra den ene retning. Så
   kører ruten forbi målet, vender, og kommer tilbage. Målt eksempel fra
   Vadstrupvej ved Hillerødmotorvejen: 977 m rute mod 167 m fugleflugt.

   Det kan ikke løses med ruteparametre: proxyen tdkosrmmapsservice
   videresender kun koordinaterne og kasserer hele query-strengen (verificeret
   med continue_straight, bearings, exclude, steps og overview - ingen af dem
   har effekt). Derfor renser vi punktlisten i stedet.

   Fremgangsmåde: rut én gang, find korte ben hvor ruten er uforholdsmæssigt
   lang, fjern de punkter, og rut igen. Et fjernet punkt lægges tilbage, hvis
   den nye rute ikke længere kommer tæt forbi det - så mister vi kun punkter,
   der ikke bidrog med information.
   --------------------------------------------------------------------------- */

/** Et ruteopslag som leaflet-routing-machine leverer det. */
export interface RoutedPath {
  coordinates: L.LatLng[];
  waypointIndices?: number[];
}

export type RouteProbe = (waypoints: L.LatLng[]) => Promise<RoutedPath | null>;

/** Ruten skal være mindst så mange gange længere end fugleflugten, før et punkt er mistænkeligt. */
const DETOUR_FACTOR = 3;

/** Kun ben kortere end dette betragtes. Et langt ben med stor faktor er en reel omvej. */
const MAX_LOCAL_SPAN_M = 500;

/** Et fjernet punkt skal stadig ligge så tæt på den nye rute, ellers lægges det tilbage. */
const NEAR_ROUTE_M = 30;

export async function removeSnapArtefacts(
  waypoints: L.LatLng[],
  probe: RouteProbe
): Promise<L.LatLng[]> {
  if (waypoints.length < 3) {
    return waypoints;
  }

  const first = await probe(waypoints);
  const suspects = findSuspiciousWaypoints(waypoints, first);
  if (suspects.size === 0) {
    return waypoints;
  }

  const cleaned = waypoints.filter((_, i) => !suspects.has(i));
  const second = await probe(cleaned);

  // Uden en ny rute at måle mod kan vi ikke stå inde for fjernelsen - behold alt.
  if (!second?.coordinates?.length) {
    return waypoints;
  }

  const restored = new Set<number>();
  suspects.forEach(i => {
    if (distanceToPath(waypoints[i], second.coordinates) > NEAR_ROUTE_M) {
      restored.add(i);
    }
  });

  if (restored.size === 0) {
    return cleaned;
  }

  return waypoints.filter((_, i) => !suspects.has(i) || restored.has(i));
}

/** Indekser på punkter hvis ben er kort i luftlinje, men langt at køre. Endepunkter røres aldrig. */
function findSuspiciousWaypoints(waypoints: L.LatLng[], routed: RoutedPath | null): Set<number> {
  const suspects = new Set<number>();
  const indices = routed?.waypointIndices;

  if (!indices || indices.length !== waypoints.length || !routed.coordinates?.length) {
    return suspects;
  }

  for (let leg = 0; leg < waypoints.length - 1; leg++) {
    const span = waypoints[leg].distanceTo(waypoints[leg + 1]);
    if (span < 1 || span > MAX_LOCAL_SPAN_M) {
      continue;
    }

    const driven = pathLength(routed.coordinates, indices[leg], indices[leg + 1]);
    if (driven / span < DETOUR_FACTOR) {
      continue;
    }

    // Omvejen skyldes ankomstretningen til benets slutpunkt; det er punktet der skal væk.
    const target = leg + 1;
    if (target > 0 && target < waypoints.length - 1) {
      suspects.add(target);
    }
  }

  return suspects;
}

/** Længden langs rutelinjen mellem to indeks. */
function pathLength(coordinates: L.LatLng[], from: number, to: number): number {
  let total = 0;
  for (let i = Math.max(0, from); i < Math.min(to, coordinates.length - 1); i++) {
    total += coordinates[i].distanceTo(coordinates[i + 1]);
  }
  return total;
}

/** Korteste afstand fra et punkt til rutelinjen, målt mod linjestykkerne og ikke kun knækpunkterne. */
function distanceToPath(point: L.LatLng, coordinates: L.LatLng[]): number {
  let best = Infinity;
  for (let i = 0; i < coordinates.length - 1; i++) {
    best = Math.min(best, distanceToSegment(point, coordinates[i], coordinates[i + 1]));
    if (best === 0) {
      break;
    }
  }
  return best;
}

const METRES_PER_DEGREE_LAT = 111320;

function distanceToSegment(p: L.LatLng, a: L.LatLng, b: L.LatLng): number {
  // Plan tilnærmelse. Over nogle få hundrede meter er fejlen forsvindende.
  const metresPerDegreeLon = METRES_PER_DEGREE_LAT * Math.cos((a.lat + b.lat) / 2 * Math.PI / 180);

  const px = (p.lng - a.lng) * metresPerDegreeLon;
  const py = (p.lat - a.lat) * METRES_PER_DEGREE_LAT;
  const bx = (b.lng - a.lng) * metresPerDegreeLon;
  const by = (b.lat - a.lat) * METRES_PER_DEGREE_LAT;

  const lengthSquared = bx * bx + by * by;
  const t = lengthSquared > 0
    ? Math.max(0, Math.min(1, (px * bx + py * by) / lengthSquared))
    : 0;

  const dx = px - t * bx;
  const dy = py - t * by;
  return Math.sqrt(dx * dx + dy * dy);
}

/** Bygger et ruteopslag mod OSRM, til brug for rensningen ovenfor. */
export function createRouteProbe(LAny: any): RouteProbe {
  const router = new LAny.Routing.osrmv1({
    serviceUrl: OSRM_SERVICE_URL,
    profile: 'driving',
    useHints: false,
    routingOptions: OSRM_ROUTING_OPTIONS
  });

  return (waypoints: L.LatLng[]) => new Promise<RoutedPath | null>(resolve => {
    router.route(
      waypoints.map(latLng => ({ latLng })),
      (err: any, routes: RoutedPath[]) => resolve(err || !routes?.length ? null : routes[0])
    );
  });
}
